import { rufe } from './client';

/**
 * Der Bausteinkatalog (PRD-29, PRD-30).
 *
 * Ein Baustein ist die kleinste Einheit, aus der ein Training besteht — und
 * dasselbe Wort steht an drei Stellen: im Programm eines Produkts, in der
 * Eignung eines Moduls und im Tagesplan. Deshalb ein Katalog und keine
 * Zeilen je Produkt.
 */

/** PRD-29, abschliessend. */
export type Bausteinart = 'begruessung' | 'theorie' | 'fahrmodul' | 'pause' | 'abschluss';

export const BAUSTEINARTEN: readonly Bausteinart[] = [
  'begruessung',
  'theorie',
  'fahrmodul',
  'pause',
  'abschluss',
];

/** Die Beschriftung je Art. Sie steht hier, damit sie ueberall gleich lautet. */
export const BAUSTEINARTNAME: Readonly<Record<Bausteinart, string>> = {
  begruessung: 'Begrüßung',
  theorie: 'Theorie',
  fahrmodul: 'Fahrmodul',
  pause: 'Pause',
  abschluss: 'Abschluss',
};

export type Bausteinzeile = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly art: Bausteinart;
  /** AeA-07: in Blöcken zu einer Viertelstunde. 45 min = 3, 60 min = 4. */
  readonly regeldauerBloecke: number;
  /** PRD-39: Wird dieser Baustein dem Kunden gegenüber benannt? */
  readonly imLeistungsversprechen: boolean;
  /**
   * AeA-08: In welchem Zeitfenster dieser Baustein üblicherweise BEGINNT.
   *
   * `null` heißt „jederzeit". Eine Abweichung ist ein Hinweis und kein Fehler
   * (TAG-13) — ein Training, das um 13:00 anfängt, isst später zu Mittag.
   */
  readonly startfensterVon: string | null;
  readonly startfensterBis: string | null;
  readonly beschreibung: string | null;
  readonly aktiv: boolean;
  readonly verwendungProdukte: number;
  readonly verwendungModule: number;
  /** PRD-30: ueber null ist die Art fest. */
  readonly verwendungPlaene: number;
};

export type Bausteineingabe = {
  readonly bezeichnung: string;
  readonly art: Bausteinart;
  readonly regeldauerBloecke: number;
  /** PRD-39: Wird dieser Baustein dem Kunden gegenüber benannt? */
  readonly imLeistungsversprechen: boolean;
  /**
   * AeA-08: In welchem Zeitfenster dieser Baustein üblicherweise BEGINNT.
   *
   * `null` heißt „jederzeit". Eine Abweichung ist ein Hinweis und kein Fehler
   * (TAG-13) — ein Training, das um 13:00 anfängt, isst später zu Mittag.
   */
  readonly startfensterVon: string | null;
  readonly startfensterBis: string | null;
  readonly beschreibung: string | null;
};

export async function bausteine(): Promise<{
  readonly bausteine: readonly Bausteinzeile[];
  /** AeA-07: null heisst „die Standorte führen verschiedene Raster". */
  readonly blocklaengeMinuten: number | null;
}> {
  return rufe('/bausteine');
}

export async function bausteinAnlegen(
  eingabe: Bausteineingabe & { readonly schluessel: string },
): Promise<{ readonly schluessel: string }> {
  return rufe('/bausteine', { verfahren: 'POST', rumpf: eingabe });
}

export async function bausteinAendern(
  schluessel: string,
  eingabe: Bausteineingabe & { readonly aktiv: boolean },
): Promise<{ readonly geaendert: true }> {
  return rufe(`/bausteine/${schluessel}`, { verfahren: 'PATCH', rumpf: eingabe });
}

/*
 * ============================================================================
 * DER RESSOURCENBEDARF (PRD-29)
 * ============================================================================
 *
 * „Jeder Baustein trägt Regeldauer in Blöcken UND SEINEN RESSOURCENBEDARF."
 * Ein Mal gepflegt, an jedem Tag gültig: Dass „Bremsen und Ausweichen" ein
 * Fahrmodul und einen Trainer braucht, ändert sich nicht von Dienstag auf
 * Mittwoch. Die Tagesplanung liest es und muss nicht fragen.
 */

/** Die sieben Arten aus `ressource_art_check`. */
export const RESSOURCENARTEN = [
  'modul',
  'raum',
  'fahrzeug',
  'betriebsmittel',
  'trainer',
  'dienstleister',
  'sonstiges',
] as const;

export type Ressourcenart = (typeof RESSOURCENARTEN)[number];

export const RESSOURCENARTNAME: Readonly<Record<Ressourcenart, string>> = {
  modul: 'Fahrmodul / Übungsfläche',
  raum: 'Schulungsraum',
  fahrzeug: 'Fahrzeug',
  betriebsmittel: 'Betriebsmittel',
  trainer: 'Trainer',
  dienstleister: 'Dienstleister',
  sonstiges: 'Sonstiges',
};

export type Bedarfszeile = {
  readonly id: string;
  readonly reihenfolge: number;
  readonly bezeichnung: string;
  readonly ressourcenart: string | null;
  readonly eigenschaftart: string | null;
  readonly eigenschaftschluessel: string | null;
  readonly anzahl: number;
  /** Ohne ihn lässt sich der Tag nicht freigeben (TAG-24). */
  readonly zwingend: boolean;
  readonly buchungsrelevant: boolean;
  /** TAG-23: Rüst- und Wechselzeit — am Bedarf, nicht am Tagesplan. */
  readonly vorbereitungMinuten: number;
  readonly nachbereitungMinuten: number;
  readonly bemerkung: string | null;
};

/**
 * Eine Eigenschaft, die im Bestand vorkommt (RES-02, RES-05).
 *
 * Ein Bedarf sucht nach der ART einer Ressource oder nach einer
 * EIGENSCHAFT. Die Arten sind sieben feste Wörter; welche Eigenschaften es
 * gibt, pflegt das Haus — „Bistro", „Nassfläche". Deshalb kommen sie vom
 * Server und stehen nicht hier.
 *
 * `ressourcen` ist die Zahl der Ressourcen, die sie tragen: Eine Eigenschaft
 * ohne Träger deckt keinen Bedarf, und das soll man vor dem Klick sehen.
 */
export type Bedarfseigenschaft = {
  readonly art: string;
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly ressourcen: number;
};

export type Bedarfseingabe = {
  readonly reihenfolge: number;
  readonly bezeichnung: string;
  readonly ressourcenart: string | null;
  /** RES-05: Art ODER Eigenschaft — genau eines von beiden. */
  readonly eigenschaftart?: string | null;
  readonly eigenschaftschluessel?: string | null;
  readonly anzahl: number;
  readonly zwingend: boolean;
  readonly buchungsrelevant: boolean;
  readonly vorbereitungMinuten: number;
  readonly nachbereitungMinuten: number;
};

/**
 * Der gepflegte Bedarf und der Vorschlag zu dieser Bausteinart.
 *
 * `vorschlag` sagt, wie der Regelfall aussieht — ein Fahrmodul braucht ein
 * Fahrmodul, eine Theoriestunde einen Raum. Er ist **nirgends gespeichert**:
 * Übernommen wird er über denselben `bedarfSetzen`, mit dem auch eine
 * getippte Zeile gespeichert wird. Ein impliziter Standardbedarf wäre die
 * bequemere Lösung und die schlechtere — man sähe einem Baustein dann nicht
 * mehr an, was er verlangt.
 */
export async function bedarf(schluessel: string): Promise<{
  readonly bedarf: readonly Bedarfszeile[];
  readonly vorschlag: readonly Bedarfseingabe[];
  readonly eigenschaften: readonly Bedarfseigenschaft[];
}> {
  return rufe(`/bausteine/${encodeURIComponent(schluessel)}/bedarf`);
}

export async function bedarfSetzen(
  schluessel: string,
  eintraege: readonly Bedarfseingabe[],
): Promise<{ readonly gesetzt: number }> {
  return rufe(`/bausteine/${encodeURIComponent(schluessel)}/bedarf`, {
    verfahren: 'PUT',
    rumpf: { bedarf: eintraege },
  });
}
